import { memo, useEffect, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import type { Chart, ChartArea, ScriptableContext } from 'chart.js';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import cn from 'classnames';

import { cleanupCustomTooltip, createCustomTooltip } from '@/shared/lib/chart';
import { useTheme } from '@/shared/lib/hooks';
import { Card } from '@/shared/ui/card';
import { GradientBadge } from '@/shared/ui/value-formatter';

import cls from './pattern-outcome-doughnut.module.css';

ChartJS.register(ArcElement, Tooltip, Legend);

export interface OutcomeData {
    type: string;
    count: number;
    percentage: number;
    variant: 'green' | 'red' | 'blue';
}

interface PatternOutcomeDoughnutProps {
    title?: string;
    centerCount: string;
    outcomes: OutcomeData[];
    layout?: 'vertical' | 'horizontal';
    className?: string;
    isFollowingCursor?: boolean;
    isCollapsible?: boolean;
    isAggregationMode?: boolean;
}

interface DoughnutChart extends Chart<'doughnut'> {
    ctx: CanvasRenderingContext2D;
    chartArea: ChartArea;
}

export const PatternOutcomeDoughnut = memo(
    ({
        title,
        centerCount,
        outcomes,
        layout = 'vertical',
        className = '',
        isFollowingCursor = false,
        isCollapsible = false,
        isAggregationMode = false,
    }: PatternOutcomeDoughnutProps) => {
        const { t } = useTranslation('pattern-studio');
        const chartRef = useRef<Chart<'doughnut'>>();
        const isDarkTheme = useTheme()[2];

        useEffect(
            () => () => {
                cleanupCustomTooltip();
            },
            [],
        );

        const chartData = {
            labels: outcomes.map((item) => item.type),
            datasets: [
                {
                    data: outcomes.map((item) => item.percentage),
                    backgroundColor: (
                        context: ScriptableContext<'doughnut'>,
                    ) => {
                        const {
                            chart: { ctx, chartArea },
                        } = context;

                        if (!chartArea) {
                            return 'transparent';
                        }

                        const centerX = (chartArea.left + chartArea.right) / 2;
                        const centerY = (chartArea.top + chartArea.bottom) / 2;
                        const radius =
                            Math.min(
                                chartArea.right - chartArea.left,
                                chartArea.bottom - chartArea.top,
                            ) / 2;

                        const gradients = isDarkTheme
                            ? [
                                  {
                                      start: {
                                          r: 0.3216,
                                          g: 0.7686,
                                          b: 0.102,
                                      },
                                      end: {
                                          r: 0.1542,
                                          g: 0.3686,
                                          b: 0.0489,
                                      },
                                  },
                                  {
                                      start: {
                                          r: 0.8275,
                                          g: 0.1255,
                                          b: 0.1608,
                                      },
                                      end: {
                                          r: 0.322,
                                          g: 0.0467,
                                          b: 0.0605,
                                      },
                                  },
                                  {
                                      start: {
                                          r: 0.1216,
                                          g: 0.3059,
                                          b: 0.8039,
                                      },
                                      end: {
                                          r: 0.0635,
                                          g: 0.1267,
                                          b: 0.2976,
                                      },
                                  },
                              ]
                            : [
                                  {
                                      start: {
                                          r: 0.3216,
                                          g: 0.7686,
                                          b: 0.102,
                                      },
                                      end: {
                                          r: 0.2,
                                          g: 0.5137,
                                          b: 0.0431,
                                      },
                                  },
                                  {
                                      start: {
                                          r: 0.8275,
                                          g: 0.1255,
                                          b: 0.1608,
                                      },
                                      end: {
                                          r: 0.5804,
                                          g: 0.0941,
                                          b: 0.1059,
                                      },
                                  },
                                  {
                                      start: {
                                          r: 0.1216,
                                          g: 0.3059,
                                          b: 0.8039,
                                      },
                                      end: {
                                          r: 0.0745,
                                          g: 0.2314,
                                          b: 0.6627,
                                      },
                                  },
                              ];

                        const gradient = ctx.createRadialGradient(
                            centerX,
                            centerY,
                            0,
                            centerX,
                            centerY,
                            radius,
                        );

                        const colors = gradients[context.dataIndex];

                        if (colors) {
                            const { start, end } = colors;

                            gradient.addColorStop(
                                0,
                                `rgb(${Math.round(start.r * 255)}, ${Math.round(start.g * 255)}, ${Math.round(start.b * 255)})`,
                            );

                            gradient.addColorStop(
                                1,
                                `rgb(${Math.round(end.r * 255)}, ${Math.round(end.g * 255)}, ${Math.round(end.b * 255)})`,
                            );
                        }

                        return gradient;
                    },
                    borderWidth: 0,
                    weight: 1.5,
                },
            ],
        };

        const variantColorMap: Record<string, string> = {
            green: 'rgba(82, 196, 26, 1)',
            red: 'rgba(211, 47, 47, 1)',
            blue: 'rgba(31, 78, 205, 1)',
        };

        const chartOptions = {
            cutout: '55%',
            spacing: 2,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                    external: createCustomTooltip({
                        isDark: isDarkTheme,
                        title: t('patternStats.outcomeDistribution'),
                        formatItems: (tooltipModel) =>
                            tooltipModel.dataPoints.map((point) => {
                                const outcome = outcomes[point.dataIndex];
                                const color =
                                    (outcome &&
                                        variantColorMap[outcome.variant]) ||
                                    'rgba(128, 128, 128, 1)';

                                return {
                                    color,
                                    label: point.label || '',
                                    value: `${point.parsed} (${outcome?.count || 0} ${t('patternStats.occurrences')})`,
                                };
                            }),
                    }),
                },
                doughnutCenterText: {
                    text: centerCount,
                    color: isDarkTheme ? 'white' : 'black',
                    fontSize: 32,
                },
            },
            maintainAspectRatio: false,
        };

        const plugins = [
            {
                id: 'doughnutCenterText',
                beforeDraw(chart: DoughnutChart) {
                    const {
                        ctx,
                        chartArea: { width, height },
                    } = chart;
                    const { text, fontSize, color } =
                        chartOptions.plugins.doughnutCenterText;

                    ctx.restore();
                    ctx.font = `${fontSize}px Inter`;
                    ctx.textBaseline = 'middle';
                    ctx.textAlign = 'center';
                    ctx.fillStyle = color;

                    const textX = width / 2;
                    const textY = height / 2;

                    ctx.fillText(text, textX, textY);
                    ctx.save();
                },
            },
        ];

        const content = (
            <div
                className={cn(
                    layout === 'vertical'
                        ? cls.rootVertical
                        : cls.rootHorizontal,
                    { [cls.aggregation]: isAggregationMode },
                )}
            >
                <div className={cls.chart}>
                    <Doughnut
                        key={`doughnut-${centerCount}-${JSON.stringify(outcomes)}`}
                        ref={chartRef}
                        data={chartData}
                        options={chartOptions}
                        plugins={plugins}
                    />
                </div>

                <div className={cls.table}>
                    <div className={cls.tableHeader}>
                        <div className={cls.cell}>{t('patternStats.type')}</div>
                        <div className={cls.cell}>
                            {t('patternStats.count')}
                        </div>
                        <div className={cls.cell}>
                            {t('patternStats.percentage')}
                        </div>
                    </div>

                    <div className={cls.tableBody}>
                        {outcomes.map((item) => (
                            <div
                                key={item.type}
                                className={cls.tableRow}
                            >
                                <div className={cls.cell}>{item.type}</div>
                                <div className={cls.cell}>{item.count}</div>
                                <div className={cls.cell}>
                                    <GradientBadge
                                        label={`${item.percentage}%`}
                                        variant={item.variant}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );

        if (!title) {
            return content;
        }

        return (
            <Card
                className={className}
                isCollapsible={isCollapsible}
                isFollowingCursor={isFollowingCursor}
                title={title}
            >
                {content}
            </Card>
        );
    },
);

PatternOutcomeDoughnut.displayName = 'PatternOutcomeDoughnut';
